/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Descendant } from "slate";

import { BasePlateSnapshot } from "./plate.entity";
/* -------------------------------------------------- */
/*                   Main Block Type                  */
/* -------------------------------------------------- */
export type BlockSnapshot =
  | TextBlockSnapshot
  | ImageBlockSnapshot
  | LinkBlockSnapshot
  | ColorBlockSnapshot
  | FileBlockSnapshot
  | CodeBlockSnapshot
  | BlockWindowSnapshot
  | PlateWindowSnapshot;

/* -------------------------------------------------- */
/*                     Block Base                     */
/* -------------------------------------------------- */
export interface BaseBlockSnapshot {
  id: string;
  createdAt: string;
  isLiked: boolean;
  connectionCount: number;
  top: number;
  left: number;
  width: number;
  height: number;
  zIndex: number;
  plateId: string;
  currentUserIsOwner: boolean;
  userId: string;
}
/* -------------------------------------------------- */
/*                    Block Types                     */
/* -------------------------------------------------- */
export enum BlockType {
  Text = "text",
  Code = "code",
  Color = "color",
  Image = "image",
  Link = "linkEmbed",
  File = "file",
  BlockWindow = "blockWindow",
  PlateWindow = "pageWindow",
}

export interface TextBlockSnapshot extends BaseBlockSnapshot {
  type: BlockType.Text;
  textContent: Descendant[];
  rawContent?: any; //@todo remove this
  textType: TextType;
  plainText: string;
}

export interface CodeBlockSnapshot extends BaseBlockSnapshot {
  type: BlockType.Code;
  plainText: string;
  language: ProgrammingLanguage;
}

export interface ImageBlockSnapshot extends BaseBlockSnapshot, FileInformation {
  type: BlockType.Image;
  imageFit: ImageFit;
  tags: ImageTag[];
  caption: string | null;
  hasCaption: boolean;
}

export interface LinkBlockSnapshot extends BaseBlockSnapshot {
  type: BlockType.Link;
  linkEmbedType: LinkEmbedType;
  url: string;
}

export interface ColorBlockSnapshot extends BaseBlockSnapshot {
  type: BlockType.Color;
  hex: string;
}

export interface FileBlockSnapshot extends BaseBlockSnapshot, FileInformation {
  type: BlockType.File;
}

export interface PlateWindowSnapshot extends BaseBlockSnapshot {
  type: BlockType.PlateWindow;
  initialPlateSnapshot: BasePlateSnapshot | null; //can be null if plate was deleted or made private. we should add flags from the backend for these cases
}

export interface BlockWindowSnapshot extends BaseBlockSnapshot {
  type: BlockType.BlockWindow;
  initialBlockSnapshot: BlockSnapshot | null; // can be null if block was deleted or taken private
}

export interface FileInformation {
  filename: string | null;
  filesize: number | null;
  originalFilename: string | null;
  fileStatus: FileStatus;
  uploadPercent: number;
}

export type ImageTag = {
  tag: string;
  type: ImageTagType;
};

/* -------------------------------------------------- */
/*                    Helper Enums                    */
/* -------------------------------------------------- */
export enum ImageTagType {
  Custom,
  Generated,
}
export enum TextType {
  Plain = "plain",
  Quote = "quote",
  Mono = "monospace",
  H1 = "headerOne",
  H2 = "headerTwo",
}

export enum ProgrammingLanguage {
  Javascript = "Javascript",
  Java = "Java",
  Typescript = "Typescript",
  Markup = "Markup",
  Python = "Python",
  Hoon = "Hoon",
  LaTeX = "LaTeX",
  C = "C",
  CPP = "CPP",
  HCL = "HCL",
  R = "R",
  OCaml = "OCaml",
  SQL = "SQL",
  Markdown = "Markdown",
  JSON = "JSON",
  HTML = "HTML",
  CSS = "CSS",
  JSX = "JSX",
  Bash = "Bash",
  Go = "Go",
  Swift = "Swift",
  Rust = "Rust",
}

export enum ImageFit {
  Cover = "cover",
  Contain = "contain",
}

export enum LinkEmbedType {
  Spotify = "spotifyEmbed",
  Twitter = "twitterEmbed",
  Youtube = "youtubeEmbed",
  Instagram = "instagramEmbed",
  Other = "linkEmbed",
}

export enum FileStatus {
  Uploading = "UPLOADING",
  Uploaded = "UPLOADED",
  Failed = "FAILED",
  None = "NONE",
}
